import { Close } from '@carbon/icons-react';
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';
import { useId, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@/shared/lib/cx';
import { usePrefersReducedMotion } from '@/shared/lib/hooks';
import { IconButton } from './IconButton';
import s from './Overlay.module.css';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  side?: 'right' | 'left';
  /** Ширина панели, px (на узких экранах — во весь экран). */
  width?: number;
  /** Тело без внутренних отступов (навигация, списки). */
  flush?: boolean;
  /** Элемент, получающий фокус при открытии. По умолчанию — сама панель (диктор читает заголовок). */
  initialFocusRef?: RefObject<HTMLElement | null>;
  closeOnBackdrop?: boolean;
}

/** Выдвижная панель (помощь, навигация на узких экранах, подробности записи без ухода со страницы). */
export function Drawer({ open, onClose, title, children, footer, side = 'right', width = 480, flush = false, initialFocusRef, closeOnBackdrop = true }: DrawerProps) {
  const { t } = useTranslation('ui');
  const reducedMotion = usePrefersReducedMotion();
  const titleId = useId();
  const { refs, context } = useFloating({ open, onOpenChange: (next) => (next ? undefined : onClose()) });
  const dismiss = useDismiss(context, { outsidePress: closeOnBackdrop, outsidePressEvent: 'mousedown' });
  const role = useRole(context, { role: 'dialog' });
  const { getFloatingProps } = useInteractions([dismiss, role]);
  const offscreen = side === 'right' ? 'translateX(100%)' : 'translateX(-100%)';
  const { isMounted, styles } = useTransitionStyles(context, {
    duration: reducedMotion ? 0 : { open: 240, close: 150 },
    initial: { transform: offscreen },
    open: { transform: 'translateX(0)' },
  });
  if (!isMounted) return null;
  return (
    <FloatingPortal>
      <FloatingOverlay className={s.drawerBackdrop} lockScroll>
        <FloatingFocusManager context={context} initialFocus={initialFocusRef ?? refs.floating} returnFocus>
          <div
            ref={refs.setFloating}
            className={cx(s.drawer, side === 'right' ? s.drawerRight : s.drawerLeft)}
            style={{ ...styles, '--drawer-width': `${width}px` } as CSSProperties}
            aria-labelledby={titleId}
            aria-modal="true"
            {...getFloatingProps()}
          >
            <div className={s.drawerHeader}>
              <h2 id={titleId} className={s.drawerTitle}>
                {title}
              </h2>
              <IconButton label={t('close')} icon={Close} onClick={onClose} tooltipPlacement="left" />
            </div>
            <div className={cx(s.drawerBody, flush && s.drawerBodyFlush)}>{children}</div>
            {footer ? <div className={s.drawerFooter}>{footer}</div> : null}
          </div>
        </FloatingFocusManager>
      </FloatingOverlay>
    </FloatingPortal>
  );
}
